import { IAddress, IPhone, IImage, IPermissionForKey, IBaseInfoBlock } from "shared/types";
import { IUpload } from "shared/services/UploadService";

export enum IMainInfoBlock {
  logistics = "logistics",
  regulations = "regulations",
  electricity = "electricity",
  wirelessFrequencies = "wirelessFrequencies",
  communication = "communication",
  dressingRooms = "dressingRooms",
  offices = "offices",
  catering = "catering",
  merchandise = "merchandise",
}

export enum IInfoBlockType {
  parking = "parking",
  loadInLoadOut = "loadInLoadOut",
  drivingPermits = "drivingPermits",
  earliestGetIn = "earliestGetIn",
  curfew = "curfew",
  soundLimit = "soundLimit",
  ageRestriction = "ageRestriction",
  pyroFireworksConfetti = "pyroFireworksConfetti",
  powerSound = "powerSound",
  powerLights = "powerLights",
  powerVideo = "powerVideo",
  powerRigging = "powerRigging",
  powerBackline = "powerBackline",
  powerFOH = "powerFOH",
  freeAllowedFrequencies = "freeAllowedFrequencies",
  frequenciesWithLicense = "frequenciesWithLicense",
  remarks = "remarks",
  internet = "internet",
  phone = "phone",
  fax = "fax",
  rooms = "rooms",
  toilets = "toilets",
  showers = "showers",
  offices = "offices",
  cateringArea = "cateringArea",
  kitchenFacilities = "kitchenFacilities",
  cateringPower = "cateringPower",
  boot = "boot",
  concession = "concession",
  fee = "fee",
  sellers = "sellers",
}

export interface IVenueBaseInfoBlock extends IBaseInfoBlock {
  type: IInfoBlockType;
}

export type IVenueBaseInfoBlocks = {
  [key in IInfoBlockType]?: IVenueBaseInfoBlock;
};

export interface IStageInfoBlock extends IVenueBaseInfoBlock {
  stage: string;
}

export interface IVenueInfoBlock extends IVenueBaseInfoBlock {
  venue: string;
}

export interface IInfoBlockWithPath extends IVenueBaseInfoBlock {
  path: string;
}

export type IInfoBlockId = {
  infoBlock: { data: { id: string } } | null;
  infoBlockType: IInfoBlockType;
};

export enum IMetaStatus {
  pending = "pending",
  accepted = "accepted",
  denied = "denied",
}

export interface IMeta {
  completeness?: number;
  createdByTm?: boolean;
  createdByVenue?: boolean;
  createdByBand?: boolean;
  public?: boolean;
  status?: IMetaStatus;
}

export interface IStage {
  id: string;
  title: string;
  info?: string;
  weight?: number;
  createdAt?: string;
  updatedAt?: string;
  dimensions?: {
    room?: IDimensions;
    stage?: IDimensions;
  };
  distanceFromLoadingToStage?: number;
  stageWeightPerSquareMeter?: number;
  stageCapacities?: IStageCapacity[];
  equipmentCategories?: IStageEquipmentCategory[];
  permissions?: IPermissionForKey[];
  infoBlocks: IInfoBlockId[];
}

export enum IStageCapacityType {
  seated = "seated",
  standing = "standing",
  generalAdmission = "generalAdmission",
  mixed = "mixed",
}

export interface IStageCapacity {
  id: string;
  title: string;
  capacity: number;
  type: keyof typeof IStageCapacityType;
}

export interface IDimensions {
  width: number | null;
  height: number | null;
  depth: number | null;
}

export interface IBaseEquipment {
  id?: string;
  brand?: string;
  amount?: number;
  dimensions?: IDimensions;
  weight?: number;
  category: IEquipmentCategory;
  model?: string;
  remark?: string;
}

export interface IEquipment extends IBaseEquipment {
  stages: { id: string }[];
}

export interface IPatchEquipment extends IBaseEquipment {
  stages: string[];
}

export enum IEquipmentCategory {
  technical = "technical",
  technicalForklift = "technicalForklift",
  technicalTelescopicLift = "technicalTelescopicLift",
  technicalCherrypicker = "technicalCherrypicker",
  technicalRisers = "technicalRisers",
  technicalBarriers = "technicalBarriers",
  rigging = "rigging",
  riggingPoints = "riggingPoints",
  riggingHoists = "riggingHoists",
  riggingControls = "riggingControls",
  riggingTrusses = "riggingTrusses",
  sound = "sound",
  soundMainPA = "soundMainPA",
  soundDelaySpeakers = "soundDelaySpeakers",
  soundFrontfills = "soundFrontfills",
  soundInfills = "soundInfills",
  soundOutfills = "soundOutfills",
  soundPAAmplifiers = "soundPAAmplifiers",
  soundProcessing = "soundProcessing",
  soundFOHDesk = "soundFOHDesk",
  soundFOHOutboard = "soundFOHOutboard",
  soundMONDesk = "soundMONDesk",
  soundMONOutboard = "soundMONOutboard",
  soundMonitors = "soundMonitors",
  soundSidefills = "soundSidefills",
  soundDrumfill = "soundDrumfill",
  soundMONAmplifiers = "soundMONAmplifiers",
  soundMicrophones = "soundMicrophones",
  soundWirelessEquipment = "soundWirelessEquipment",
  soundMicrophoneStands = "soundMicrophoneStands",
  soundDJEquipment = "soundDJEquipment",
  soundMiscellaneous = "soundMiscellaneous",
  light = "light",
  lightLightDesk = "lightLightDesk",
  lightIntelligentLights = "lightIntelligentLights",
  lightConventionalLights = "lightConventionalLights",
  lightDimmers = "lightDimmers",
  lightFollowSpots = "lightFollowSpots",
  lightSpecials = "lightSpecials",
  lightSmoke = "lightSmoke",
  lightHaze = "lightHaze",
  lightFX = "lightFX",
  lightMiscellaneous = "lightMiscellaneous",
  video = "video",
  videoProjector = "videoProjector",
  videoProjectionScreen = "videoProjectionScreen",
  videoLEDScreen = "videoLEDScreen",
  videoVideoMixer = "videoVideoMixer",
  videoMiscellaneous = "videoMiscellaneous",
}

export interface IEquipmentCategoryItem extends IEquipmentSubcategoryItem {
  slug: string;
  categories: IEquipmentSubcategoryItem[];
}

export interface IEquipmentSubcategoryItem extends IStageEquipmentCategory {
  label: string;
}

export interface IStageEquipmentCategory {
  category: IEquipmentCategory;
  text?: string | null;
  amount?: number;
}

export interface IStagePatchData {
  equipmentCategories: IStageEquipmentCategory[];
}

export interface IEditorInvite {
  id: string;
  email: string;
  venue?: {
    title: string;
  };
}

export interface IVenue {
  id: string;
  title: string;
  alternativeName?: string;
  bandCount?: number;
  bands?: { id: string }[] | null;
  contact?: IContactInfo;
  address?: IAddress;
  createdAt?: string;
  updatedAt?: string;
  archivedAt?: string;
  branding?: IBranding;
  brandColor?: string;
  stages?: IStage[];
  logo?: IImage;
  coverImage?: IImage;
  meta?: IMeta;
  permissions?: IPermissionForKey[];
  users?: IVenueUser[];
  invites?: IEditorInvite[];
  published?: boolean;
  infoBlocks: IInfoBlockId[];
}

export interface IVenuePostData {
  title: string;
  alternativeName?: string;
  contact?: IContactInfo;
  address?: IAddress;
  bands?: string[] | null;
  notify?: boolean;
  location?: string;
}

export interface IVenuePatchData {
  title?: string;
  alternativeName?: string;
  contact?: IContactInfo;
  address?: IAddress;
  logo?: { file: IUpload } | null;
  coverImage?: { file: IUpload } | null;
  brandColor?: string;
  users?: string[];
}

export interface IBranding {
  primaryColor?: string;
  secondaryColor?: string;
}

export interface IContactInfo {
  website?: string;
  email?: string;
  phone?: IPhone;
}

export interface IVenueUser {
  id: string;
  firstname: string;
  lastname: string;
  email: string;
}

export interface ISuggestEditBreadcrumbItem {
  type: string | null;
  title: string;
}

export interface ISuggestEdit {
  email: string;
  editLink: string;
  suggestion: string;
  token: string;
  venue: string;
  breadcrumb: ISuggestEditBreadcrumbItem[];
}
